import React, { useEffect, useState } from 'react';
import { Button, Input, Select, Snackbar, Checkbox } from 'geniebook-ui/dist';
import { useNavigate } from 'react-router-dom';
import { SelectOptionType } from 'geniebook-ui/dist/components/Select';
import { useSchoolList } from '@hooks/user.hook';
import {
  IFormAcademicData,
  ISchoolDetails,
} from '@models/interfaces/user.interface';
import { useMutation } from '@tanstack/react-query';
import UserServices from '@services/GeniebookAPI/UserServices.services';
import { useViewWidth } from '@utils/helpers/viewWidth';
import { useAtomValue } from 'jotai';
import { userDetailQueryAtom } from '@atom/globalQueryAtom';

const breakpoint = require('geniebook-ui/dist/themes/breakpoint');

const AcademicForm = () => {
  const navigate = useNavigate();
  const getUserDetails = useAtomValue(userDetailQueryAtom);

  const [disableButton, setDisableButton] = useState(true);
  const [formData, setFormData] = useState<IFormAcademicData>({
    school_id: 0,
    show_school: 0,
    level_id: 0,
    class: '1',
  });
  const schoolListQuery = useSchoolList();
  const viewWidth = useViewWidth();
  const [schoolPlaceholderName, setSchoolPlaceholderName] =
    useState<string>('');
  const [showSnackbar, setShowSnackbar] = useState<boolean>(false);

  // select option list
  const [schoolSelection, setSchoolSelection] = useState<SelectOptionType[]>([
    {
      label: 'Select school',
      value: 0,
    },
  ]);
  const [levelList] = useState<SelectOptionType[]>([
    {
      label: 'Select level',
      value: 0,
    },
  ]);

  useEffect(() => {
    if (schoolListQuery.data?.data?.data) {
      const schoolList: SelectOptionType[] = [];
      schoolListQuery.data.data.data.forEach((item: ISchoolDetails) => {
        schoolList.push({
          label: item.school_name,
          value:
            typeof item.school_id === 'string'
              ? parseInt(item.school_id)
              : item.school_id,
        });

        if (item.school_id == getUserDetails?.data?.data.school_id) {
          setSchoolPlaceholderName(item.school_name);
        }
      });
      setSchoolSelection(schoolList);
    }
  }, [schoolListQuery.data?.data, getUserDetails.data?.data]);

  function handleCancel() {
    navigate('/profile');
  }

  const updateAcademicForm = useMutation(
    (formData: IFormAcademicData) => UserServices.updateUserProfile(formData),
    {
      onSuccess(data) {
        if (data.data.error) {
          switch (data.data.error_code) {
            default:
              setShowSnackbar(true);
              break;
          }
        } else {
          getUserDetails.refetch();
          navigate('/profile');
        }
      },
      onError() {
        setShowSnackbar(true);
      },
    },
  );

  function handleSave() {
    // step by step
    // 1. make request to change
    // 2. invalidate react query user detail to make it re-fetch new data
    // 3. navigate to profile
    updateAcademicForm.mutateAsync(formData);
  }

  useEffect(() => {
    if (getUserDetails.isSuccess) {
      setFormData({
        ...formData,
        level_id: parseInt(getUserDetails?.data?.data.level_id),
        show_school: getUserDetails?.data?.data.show_school,
        class: getUserDetails?.data?.data.class,
      });
    }
  }, [getUserDetails.data]);

  return (
    <div>
      {showSnackbar ? (
        <Snackbar
          message={'Oops! Something went wrong. Please try again later.'}
          views={viewWidth < breakpoint.md ? 'mobile' : 'desktop'}
          buttonText={'Dismiss'}
          onTextClick={() => {
            setShowSnackbar(false);
          }}
          states={'error'}
          className={'fixed center-horizontal top-[60px]'}
        />
      ) : (
        <></>
      )}
      <div className={'mb-6'}>
        <Select
          darkMode={true}
          isSearchable={true}
          label={'School'}
          className={
            'max-w-[352px] max-md:max-w-full &::-webkit-scrollbar:none [&>div>input]:h-12'
          }
          value={formData.school_id}
          placeholder={schoolPlaceholderName}
          options={schoolSelection}
          onChange={value => {
            setFormData({ ...formData, school_id: value as number });
            setDisableButton(false);
          }}
        />
      </div>
      <div className={'mb-6 [&>label>div>svg]:!h-6'}>
        <Checkbox
          variant={'dark'}
          label={'Show school'}
          isChecked={Boolean(Number(formData.show_school))}
          isValidation={false}
          onChange={() => {
            setFormData({
              ...formData,
              show_school: Number(formData.show_school) ? 0 : 1,
            });
            setDisableButton(false);
          }}
        />
      </div>
      <div className={'mb-6 text-primary-navyblue'}>
        <Select
          darkMode={true}
          label={'Level'}
          disabled={true}
          className={
            'max-w-[352px] max-md:max-w-full [&>div>input]:bg-neutral-notebook/[0.5] [&>div>input]:border-0 text-neutral-whiteboard [&>div>input]:h-12'
          }
          value={formData.level_id}
          placeholder={String(formData.level_id)}
          options={levelList}
        />
      </div>
      <div className={'mb-10'}>
        <Input
          mode={'dark'}
          label={'Class'}
          disabled={true}
          className={
            'md:max-w-[352px] max-md:max-w-full [&>input]:bg-neutral-notebook/[0.5] [&>input]:border-0'
          }
          value={formData.class}
        />
      </div>
      <div className={'flex'}>
        <Button
          variant={'primary'}
          className={'font-black max-md:!w-full max-md:!max-w-full'}
          onClick={handleSave}
          disabled={disableButton}
        >
          Save
        </Button>
        <Button
          variant={'secondary'}
          className={'max-lg:hidden'}
          onClick={handleCancel}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default AcademicForm;
